import { runInAction } from "mobx";
import InformacoesProcessoApi from '../api/InformacoesProcessoApi';
import { UtilData } from "../misc/UtilData";
import { IIndicadores, IMovimento, Processo } from "../modelo";
import { DadosProcessoBTV } from "../modelo/DadosProcessoBTV";
import { ClasseJudicial } from '../modelo/dominio/ClasseJudicial';
import { DadosProcessoDto } from "../modelo/dto/DadosProcessoDto";
import { PecaDto } from "../modelo/dto/PecaDto";
import { TarefasFluxoDto } from "../modelo/dto/TarefasFluxoDto";
import MensagemStore from '../store/MensagemStore';
import ProcessoStore from "../store/ProcessoStore";
import AssuntoService from "./AssuntoService";
import ProcessoExpandidoService from "./ProcessoExpandidoService";
import UtilNumeroProcesso from "../utils/UtilNumeroProcesso";
import { apresentarApiErroPadrao, extrairApiErroPadrao } from '../utils/ApiErroPadraoUtils';

export const FALHA_CONSULTA_PRINCIPAL_PROCESSO = 'falhaConsultaPrincipalProcesso';

interface OpcoesConsultaProcesso {
    carregarPecas?: boolean;
}

class InformacoesProcessoService {

    private extrairMensagemErroPartes(erro: any): string {
        const apiErro = extrairApiErroPadrao(erro);
        return [apiErro?.message, apiErro?.details]
            .filter((texto) => !!texto?.trim())
            .join('\n');
    }

    private withTimeoutReject<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error(`Timeout ao carregar ${label}.`)), ms);
            promise
                .then((value) => {
                    clearTimeout(timer);
                    resolve(value);
                })
                .catch((error) => {
                    clearTimeout(timer);
                    reject(error);
                });
        });
    }

    private withTimeoutFallback<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
        return new Promise<T>((resolve) => {
            const timer = setTimeout(() => resolve(fallback), ms);
            promise
                .then((value) => {
                    clearTimeout(timer);
                    resolve(value);
                })
                .catch(() => {
                    clearTimeout(timer);
                    resolve(fallback);
                });
        });
    }

    async consultarProcesso(idProcesso: number, identificarFalhaConsultaPrincipal: boolean = false, opcoes: OpcoesConsultaProcesso = {}): Promise<DadosProcessoDto> {
        const carregarPecas = opcoes.carregarPecas !== false;
        let dadosProcesso;
        if (identificarFalhaConsultaPrincipal) {
            try {
                dadosProcesso = await this.withTimeoutReject(
                    InformacoesProcessoApi.consultarProcesso(idProcesso),
                    20000,
                    'dados principais do processo'
                );
            } catch (erro) {
                erro[FALHA_CONSULTA_PRINCIPAL_PROCESSO] = true;
                throw erro;
            }
        } else {
            dadosProcesso = await this.withTimeoutReject(
                InformacoesProcessoApi.consultarProcesso(idProcesso),
                20000,
                'dados principais do processo'
            );
        }
        dadosProcesso.identificadorProcesso = idProcesso;
        let mensagemErroPartes = '';
        const resultados = await Promise.allSettled([
            this.withTimeoutFallback(this.consultarMovimentos(idProcesso), 15000, [] as any),
            this.withTimeoutFallback(this.consultarAssuntos(idProcesso), 15000, [] as any),
            this.withTimeoutFallback(this.consultarIndicadores(idProcesso), 15000, [] as any),
            this.withTimeoutFallback(
                this.consultarPartesProcesso(idProcesso).catch((erro) => {
                    mensagemErroPartes = this.extrairMensagemErroPartes(erro);
                    return undefined;
                }),
                15000,
                undefined as any
            ),
            this.withTimeoutFallback(this.consultarAlertasProcesso(idProcesso), 15000, [] as any),
            this.withTimeoutFallback(this.consultarTarefaFluxoPJe(idProcesso), 15000, undefined as any),
            carregarPecas ?
                this.withTimeoutFallback(ProcessoExpandidoService.consultarPecas(idProcesso), 15000, [] as any)
                :
                Promise.resolve([] as any)
        ]);

        dadosProcesso.movimentos = resultados[0].status === 'fulfilled' ? (resultados[0].value || []) as any : [];
        dadosProcesso.assuntoList = resultados[1].status === 'fulfilled' ? (resultados[1].value || []) as any : [];
        dadosProcesso.indicadores = resultados[2].status === 'fulfilled' ? (resultados[2].value || []) as any : [];
        dadosProcesso.partes = resultados[3].status === 'fulfilled' ? resultados[3].value as any : undefined;
        dadosProcesso.mensagemErroPartes = dadosProcesso.origem === 'L' ?
            mensagemErroPartes || undefined
            :
            undefined;
        dadosProcesso.alertas = resultados[4].status === 'fulfilled' ? (resultados[4].value || []) as any : [];
        dadosProcesso.tarefaFluxoPje = resultados[5].status === 'fulfilled' ? resultados[5].value as any : undefined;
        dadosProcesso.pecas = resultados[6].status === 'fulfilled' ? (resultados[6].value || []) as any : [];

        dadosProcesso.alertasAtivos = dadosProcesso.alertas?.filter(alerta => alerta.ativo);
        dadosProcesso.autuadoEm = UtilData.formataData(dadosProcesso.autuadoEm);
        dadosProcesso.dataAutuacaoTst = dadosProcesso.dataAutuacaoTst ? UtilData.formataData(dadosProcesso.dataAutuacaoTst, true) : dadosProcesso.autuadoEm;

        // ORDENANDO POR DATA DE ATUALIZAÇÃO (MAIS RECENTE PRIMEIRO)
        dadosProcesso.movimentos = (dadosProcesso.movimentos || []).map(movimento => {
            movimento.atualizadoEm = UtilData.formataData(movimento.atualizadoEm, true);
            return movimento;
        }).sort((m1, m2) => {
            return (new Date(m2.atualizadoEm)).getTime() - (new Date(m1.atualizadoEm)).getTime();
        });

        const procNaStore = ProcessoStore._processos.find(p => p.identificadorProcesso === idProcesso);
        if (procNaStore) {
            runInAction(() => {
                procNaStore.movimentos = dadosProcesso.movimentos;
                if (dadosProcesso.movimentos?.length > 0) {
                    procNaStore.ultimoMovimentoProcessual = dadosProcesso.movimentos[0];
                }
            });
        }

        return DadosProcessoDto.novo(dadosProcesso);
    }

    async consultarPartesProcesso(idProcesso: number) {
        const mapPartes = await InformacoesProcessoApi.consultarPartesProcesso(idProcesso);
        return mapPartes[idProcesso];
    }

    async consultarPartesDaListaDeProcesso(idsProcessos: number[]) {
        const ids = [...idsProcessos];
        const pageSize = 900;
        const pages = new Array(Math.ceil(ids.length / pageSize)).fill(null).map(_x => ids.splice(0, pageSize));
        const result = await Promise.all(pages.map(page => InformacoesProcessoApi.consultarPartesDaListaDeProcesso(page)));
        return result.reduce((m1, m2) => new Map([...m1, ...m2]), new Map());
    }

    async consultarQuantidadePeticoesPendentesDaListaDeProcesso(idsProcessos: number[]) {
        return await InformacoesProcessoApi.consultarQuantidadePeticoesPendentesDaListaDeProcesso(idsProcessos);
    }

    async consultaRetornosProcessos(idsProcessos: number[]) {
        return await InformacoesProcessoApi.consultarRetornosDaListaDeProcesso(idsProcessos);
    }


    async consultaRetornoProcesso(idProcesso: number) {
        return await InformacoesProcessoApi.consultarRetornosDeProcesso(idProcesso);
    }

    async consultarDocumentosProcesso(numeroProcesso) {
        return await InformacoesProcessoApi.consultarDocumentosProcesso(numeroProcesso);
    }

    async consultarConteudoDocumentoProcesso(idProcesso, idDocumento, tipo) {
        return await InformacoesProcessoApi.consultarConteudoDocumentoProcesso(idProcesso, idDocumento, tipo);
    }

    async consultarCodigoValidacaoDocumento(_idProcesso, _idDocumento) {
        console.info("consultarCodigoValidacaoDocumento não implementado");
    }

    async consultarAlertasProcesso(idProcesso) {
        return await InformacoesProcessoApi.consultarAlertasProcesso(idProcesso);
    }

    async consultarTarefaFluxoPJe(idProcesso): Promise<TarefasFluxoDto> {
        return await InformacoesProcessoApi.consultarTarefaFluxoPJe(idProcesso)
    }

    consultarConteudoCertidao(idProcesso, idDocumento) {
        return InformacoesProcessoApi.consultarConteudoCertidao(idProcesso, idDocumento);
    }

    consultarConteudo(idProcesso, idDocumento) {
        return InformacoesProcessoApi.consultarConteudo(idProcesso, idDocumento);
    }

    async consultarPeticoesPendentes(numeroProcesso: number) {
        return await InformacoesProcessoApi.consultarPeticoesPendentes(numeroProcesso);
    }

    async consultarPeticoes(numeroProcesso: number) {
        return await InformacoesProcessoApi.consultarPeticoes(numeroProcesso);
    }

    async consultarTodasPeticoesPendentes(ids: number[]) {
        console.info("deprecated - tente usar EscaninhoPeticaoService")
        let resultado: Map<number, PecaDto[]> = new Map;

        const peticoesPromise = await this.consultarTodasPeticoesPendentesChunked(ids);
        peticoesPromise.forEach(promise => promise.then(result => (resultado as any) = { ...resultado, ...result }));
        await Promise.all(peticoesPromise);
        return resultado;
    }

    async consultarTodasPeticoesPendentesChunked(ids: number[]) {
        const idsPaginados = this.quebraArray(ids, 900);
        const promises = idsPaginados.map(paginaIds => InformacoesProcessoApi.consultarTodasPeticoesPendentes(paginaIds) as Promise<Map<number, PecaDto[]>>);
        return promises;
    }


    quebraArray(array: number[], chunkSize = 10): number[][] {
        const result = [];
        for (let i = 0; i < array.length; i += chunkSize) {
            const chunk = array.slice(i, i + chunkSize);
            result.push(chunk);
        }
        return result;
    }

    async consultarDocumentoPorId(idProcesso, idDocumento) {
        return await InformacoesProcessoApi.consultarDocumentoPorId(idProcesso, idDocumento);
    }

    async consultarQuantidadePeticoesPendentes(numeroProcesso) {
        return await InformacoesProcessoApi.consultarQuantidadePeticoesPendentes(numeroProcesso);
    }

    async consultarIndicadores(idProcesso): Promise<IIndicadores[]> {
        const indicadores = await InformacoesProcessoApi.consultarIndicadoresDaListaDeProcesso([idProcesso]);
        return indicadores[idProcesso];
    }

    async buscarIndicadoresDaListaDeProcessos(idsProcessos: number[]) {
        const ids = [...idsProcessos];
        const pageSize = 800;
        const pages = new Array(Math.ceil(ids.length / pageSize)).fill(null).map(_x => ids.splice(0, pageSize));
        const result = await Promise.all(pages.map(page => InformacoesProcessoApi.consultarIndicadoresDaListaDeProcesso(page)));
        const resultado = [];
        result.forEach(r => {
            Object.keys(r).forEach(id => resultado[id] = r[id]);
        });
        return resultado;
    }

    async consultarMovimentosEmLote(idsProcesso: number[]): Promise<[][]> {
        return await InformacoesProcessoApi.consultarMovimentos(idsProcesso);
    }
    async consultarMovimentos(idProcesso: number): Promise<[][]> {
        let movimentos = await this.consultarMovimentosEmLote([idProcesso]);
        return movimentos[idProcesso] || [];
    }

    async consultarAssuntos(idProcesso) {
        const retorno = await AssuntoService.buscarPorProcessos([idProcesso]);
        if (retorno) {
            return retorno[idProcesso];
        }
        return [];
    }

    carregarInfosProcesso = async (processo: Processo) => {
        try {
            const idProcesso = processo.identificadorProcesso ? processo.identificadorProcesso : (processo as any).id;
            processo.identificadorProcesso = idProcesso;
            const processoTmp = await this.consultarProcesso(idProcesso);
            processo.classeJudicial = ClasseJudicial.novoComArray(processoTmp.classesJudicial);
            processo.numero = processoTmp.numero;
            if (processo.setRelator != undefined) {
                processo.setRelator(processo.relator || processoTmp.relator);
            } else {
                processo.relator = (processo.relator || processoTmp.relator);
            }
            if (processoTmp.sigUnAdm) {
                processo.sigUnAdm = processoTmp.sigUnAdm;
            }
            let partes = {};
            partes = processo.partes || await this.consultarPartesProcesso(idProcesso);
            const alertas = processo.alertas || await this.consultarAlertasProcesso(idProcesso);
            const alertasAtivos = alertas.filter(alerta => alerta.ativo);
            const tarefaFluxoPje = await this.consultarTarefaFluxoPJe(idProcesso);
            const indicadores = processo.indicadores || await this.consultarIndicadores(idProcesso);
            return {
                processo: processo,
                partes: partes,
                alertas: alertasAtivos,
                tarefaFluxoPje: tarefaFluxoPje,
                indicadores: indicadores,
            };
        } catch (error) {
            console.error(error);
            if (!apresentarApiErroPadrao(error)) {
                MensagemStore.apresentarErro(error)
            }
            return {
                processo: undefined,
                partes: undefined,
                alertas: undefined,
                tarefaFluxoPje: undefined,
                indicadores: undefined,
            };
        }
    }

    async buscarResultadoDeJulgamentoDaListaDeProcessos(idsProcessos: number[]) {
        return await InformacoesProcessoApi.buscarResultadoDeJulgamentoDaListaDeProcessos(idsProcessos);
    }

    async buscarProcessosComAgProvido(idsProcesso: number[]) {
        return await InformacoesProcessoApi.buscarProcessosComAgProvido(idsProcesso);
    }
    async buscarSituacaoInclusaoPautaDaListaDeProcessos(idsProcessos: number[]) {
        return await InformacoesProcessoApi.buscarSituacaoInclusaoPautaDaListaDeProcessos(idsProcessos);
    }

    async buscarDadosProcessoBTV(gabinete: string) {
        const dadosBTV: DadosProcessoBTV[] = await InformacoesProcessoApi.buscarPartesProcessoBTV(gabinete)
        const mapaDadosBTV = new Map();
        if (Array.isArray(dadosBTV)) {
            dadosBTV.forEach(dado => {
                mapaDadosBTV.set(UtilNumeroProcesso.numeroProcessoSemFase(dado.NUM_PROC_FORMATADO_FASE), dado)
            })
        }
        runInAction(() => {
            ProcessoStore.dadosBTV = mapaDadosBTV
        })
    }

}

export default new InformacoesProcessoService();
